import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { publicAPI } from '../services/api';
import { formatWhatsAppLink, getDefaultQuoteMessage } from '../utils/whatsapp';
import { resolveImageUrl } from '../utils/imageUrl';
import { useSiteData } from '../contexts/SiteDataContext';

const FeaturedProducts = () => {
  const navigate = useNavigate();
  const { pageContents } = useSiteData();
  const fpData = pageContents.featured_products || {};
  const sectionTitle = fpData.title || 'Componentes Hidráulicos de Alta Performance';
  const sectionSubtitle = fpData.subtitle || 'Soluções selecionadas com critério técnico para máxima eficiência, confiabilidade operacional e durabilidade do seu sistema hidráulico';
  const [products, setProducts] = useState([]);

  useEffect(() => {
    publicAPI.getProducts({ featured: true, limit: 6 })
      .then(setProducts)
      .catch(console.error);
  }, []);

  const handleQuoteClick = (e, product) => {
    e.stopPropagation();
    window.open(formatWhatsAppLink(getDefaultQuoteMessage(product.code, product.name)), '_blank');
  };

  if (products.length === 0) return null;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <Badge className="bg-[#FFB025] text-[#055509] hover:bg-[#FFB025] mb-4">
            Produtos em Destaque
          </Badge>
          <h2 className="text-4xl font-bold text-[#055509] mb-4">
            {sectionTitle}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {sectionSubtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <Card
              key={product.id}
              className="group cursor-pointer hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#FFB025] overflow-hidden"
              onClick={() => navigate(`/produto/${product.id}`)}
            >
              <div className="relative h-64 overflow-hidden bg-gray-100">
                <img src={resolveImageUrl(product.mainImage || product.images?.[0])} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNFNUU3RUIiLz48dGV4dCB4PSIyMDAiIHk9IjIwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Q0EzQUYiPlNlbSBJbWFnZW08L3RleHQ+PC9zdmc+'; }} />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-[#055509] text-white">{product.category}</Badge>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="text-sm font-mono text-[#FFB025] font-semibold mb-2">{product.code}</div>
                <h3 className="text-xl font-bold text-[#055509] mb-3 group-hover:text-[#FFB025] transition-colors">{product.name}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.shortDescription}</p>
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 mb-2">APLICAÇÕES:</div>
                  <div className="flex flex-wrap gap-2">
                    {(product.applications || []).slice(0, 2).map((app, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">{app}</span>
                    ))}
                    {(product.applications || []).length > 2 && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">+{product.applications.length - 2}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  <Button size="sm" onClick={(e) => handleQuoteClick(e, product)} className="flex-1 bg-[#FFB025] text-[#055509] hover:bg-[#055509] hover:text-white font-semibold">
                    <MessageCircle size={16} className="mr-2" />Solicitar Cotação
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/produto/${product.id}`)} className="border-[#055509] text-[#055509] hover:bg-[#055509] hover:text-white">
                    <ArrowRight size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button size="lg" variant="outline" onClick={() => navigate('/produtos')} className="border-2 border-[#055509] text-[#055509] hover:bg-[#055509] hover:text-white font-semibold px-8">
            Ver Todos os Produtos
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
